import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Calendar, Tag, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface WelcomeWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: () => void;
}

export const WelcomeWizardModal: React.FC<WelcomeWizardModalProps> = ({ isOpen, onClose, onStart }) => {
  const { t } = useLanguage();

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mb-3">
            <Sparkles className="h-7 w-7 text-primary-foreground" />
          </div>
          <DialogTitle className="text-center text-xl">
            {t('wizard.welcome.title') || 'Bienvenue sur SportLogin !'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('wizard.welcome.subtitle') || 'Configurons votre compte ensemble en quelques étapes simples.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Tag className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{t('wizard.welcome.step1') || 'Créez vos catégories'}</p>
              <p className="text-xs text-muted-foreground">{t('wizard.welcome.step1Desc') || 'Définissez vos services et tarifs.'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{t('wizard.welcome.step2') || 'Ajoutez vos élèves'}</p>
              <p className="text-xs text-muted-foreground">{t('wizard.welcome.step2Desc') || 'Constituez votre liste d\'élèves.'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{t('wizard.welcome.step3') || 'Planifiez vos cours'}</p>
              <p className="text-xs text-muted-foreground">{t('wizard.welcome.step3Desc') || 'Créez vos premiers événements.'}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="sm:flex-1">
            {t('wizard.welcome.skip') || 'Plus tard'}
          </Button>
          <Button onClick={onStart} className="sm:flex-1">
            {t('wizard.welcome.start') || 'Commencer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeWizardModal;
